import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Form, Input, Button, Card, Typography, Alert, Modal, Result } from 'antd';
import { LockOutlined, ArrowLeftOutlined, CheckCircleOutlined } from '@ant-design/icons';
import '../../components/common.css';

const { Title, Text } = Typography;

const ChangePasswordPage = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;

  const handleChangePassword = async (values) => {
    if (values.newPassword !== values.confirmNewPassword) {
      setError('New passwords do not match');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');
      await axios.put(`${API_URL}/users/${userId}/change-password`, {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setLoading(false);
      // Show custom success modal instead of default alert
      setSuccessModalVisible(true);
    } catch (error) {
      console.error('Error changing password:', error);
      setError('Failed to change password. Please ensure your current password is correct.');
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/signin');
  };

  const handleSuccessModalOk = () => {
    setSuccessModalVisible(false);
    navigate('/dashboard');
  };

  return (
    <div className="auth-container">
      <Card
        style={{
          width: '100%',
          maxWidth: '420px',
          borderRadius: '12px',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.08)',
          padding: '0',
          overflow: 'hidden'
        }}
        bodyStyle={{
          padding: '2.5rem'
        }}
      >
        
        {/* Back button */}
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={handleBack}
          style={{ 
            marginBottom: '1rem', 
            padding: '0',
            position: 'absolute',
            top: '1rem',
            left: '1rem'
          }}
        >
          Back to Sign In
        </Button>
        
        <div style={{ textAlign: 'center', marginBottom: '2rem', marginTop: '1.5rem' }}>
          <Title 
            level={2} 
            style={{ 
              margin: 0,
              fontSize: '26px',
              fontWeight: '600'
            }}
          >
            Change Password
          </Title>
          <Text type="secondary" style={{ marginTop: '0.5rem', display: 'block' }}>
            Update your account password
          </Text>
        </div>

        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            style={{ marginBottom: '1.5rem', borderRadius: '6px' }}
            closable
            onClose={() => setError('')}
          />
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleChangePassword}
          requiredMark={false}
        >
          <Form.Item
            name="currentPassword"
            label="Current Password"
            rules={[
              { required: true, message: 'Please enter your current password' }
            ]}
          >
            <Input.Password 
              prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
              placeholder="Enter your current password"
              style={{
                height: '45px',
                borderRadius: '6px'
              }}
            />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label="New Password"
            rules={[
              { required: true, message: 'Please enter a new password' },
              { min: 8, message: 'Password must be at least 8 characters long' }
            ]}
          >
            <Input.Password 
              prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
              placeholder="Enter your new password"
              style={{
                height: '45px',
                borderRadius: '6px'
              }}
            />
          </Form.Item>

          <Form.Item
            name="confirmNewPassword"
            label="Confirm New Password"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Please confirm your new password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('The two passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password 
              prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
              placeholder="Confirm your new password"
              style={{
                height: '45px',
                borderRadius: '6px'
              }}
            />
          </Form.Item>

          <Form.Item style={{ marginTop: '1.5rem' }}>
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={loading}
              style={{
                height: '45px',
                borderRadius: '6px',
                fontSize: '16px',
                fontWeight: '500',
                backgroundColor: '#1677ff',
                boxShadow: '0 2px 8px rgba(22, 119, 255, 0.15)'
              }}
            >
              Update Password
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* Success Modal */}
      <Modal
        visible={successModalVisible}
        onOk={handleSuccessModalOk}
        onCancel={handleSuccessModalOk}
        footer={[
          <Button 
            key="ok" 
            type="primary" 
            onClick={handleSuccessModalOk}
            style={{
              borderRadius: '6px',
              width: '100%',
              height: '40px'
            }}
          >
            Go to Dashboard
          </Button>
        ]}
        closable={false}
        centered
        width={320}
        bodyStyle={{ padding: '30px 15px' }}
      >
        <div style={{ textAlign: 'center' }}>
          <CheckCircleOutlined 
            style={{ 
              fontSize: '60px', 
              color: '#52c41a',
              marginBottom: '15px'
            }} 
          />
          <Title level={4}>Password Updated</Title>
          <Text type="secondary">
            Your password has been successfully changed.
          </Text>
        </div>
      </Modal>
    </div>
  );
};

export default ChangePasswordPage;